/* The bridge between the page and PCRE2.
 *
 * **Everything here exists to hide ONE mismatch**: the engine works in UTF-8
 * bytes and JavaScript works in UTF-16 code units. `l+` over "héllo" matches at
 * byte 3 and at character 2, and a tester that confuses the two highlights the
 * wrong span. It only ever goes wrong on text that is not ASCII, which is
 * exactly the text nobody pastes while developing, so the conversion is done
 * here once rather than trusted to each caller.
 *
 * The engine itself is bundled into this page's script - `Engine/engine.mjs`,
 * a committed artefact with the WebAssembly inlined, built by
 * tools/pcre2/build-engine.sh. One file, one request. PLAN.md, "Asset
 * delivery": round trips beat bytes.
 *
 * **Every call here is synchronous and runs on whichever thread called it.**
 * Since 2026-09-13 that thread is a Web Worker in the browser (`worker.js`,
 * made from this same bundle), so a slow compile never freezes the page; under
 * node the harnesses call these directly. Nothing in this file knows which.
 */

let starting = null;
let engine = null;

/**
 * The engine is instantiated ONCE and reused for every call.
 *
 * **The import is deferred to here rather than hoisted to the top of the
 * file, and that is load bearing.** esbuild turns a dynamic import of a
 * bundled module into a lazy initialiser, so the 400 KB of inlined
 * WebAssembly is only materialised on the thread that actually calls
 * `start()`: the worker. The page's own thread loads the same bundle to run
 * the tester and never pays for the engine it does not run.
 */
export function start() {
  starting ??= import("../Engine/engine.mjs")
    .then((module) => module.default())
    .then((instance) => {
      engine = instance;
      return instance;
    });

  return starting;
}

function call(name, args) {
  const answer = engine.ccall(name, "number", args.map(() => "string"), args);
  return JSON.parse(engine.UTF8ToString(answer));
}

const encoder = new TextEncoder();

/**
 * A map from BYTE offset to UTF-16 index for one string, built once per call
 * rather than per offset.
 *
 * The array is one longer than the byte length so that the end offset of a
 * match at the end of the subject resolves. Continuation bytes map to the
 * index of the character they belong to, which never matters for a real offset
 * (the engine only ever reports character boundaries) and stops a bad offset
 * producing a silent `undefined`.
 */
function byteToCharacterMap(text) {
  const bytes = encoder.encode(text);
  const map = new Int32Array(bytes.length + 1);

  let byte = 0;

  for (let character = 0; character < text.length; ) {
    const codePoint = text.codePointAt(character);
    const width = codePoint > 0xffff ? 2 : 1;
    const size = codePoint < 0x80 ? 1 : codePoint < 0x800 ? 2 : codePoint < 0x10000 ? 3 : 4;

    for (let i = 0; i < size; i++) {
      map[byte + i] = character;
    }

    byte += size;
    character += width;
  }

  map[bytes.length] = text.length;

  return map;
}

/**
 * Match, with every offset already converted to a UTF-16 index.
 *
 * Returns `{ error, at }` or `{ captures, matches }`, where each match is an
 * array of `{ start, end }` or `null` for a group that did not participate.
 * Group 0 is the match itself.
 */
export function match(pattern, subject, flags) {
  const answer = call("rx_match", [pattern, subject, flags]);

  if (answer.error) {
    return answer;
  }

  const map = byteToCharacterMap(subject);

  return {
    captures: answer.captures,
    matches: answer.matches.map((groups) =>
      groups.map(([start, end]) => (start < 0 ? null : { start: map[start], end: map[end] }))),
  };
}

/**
 * The pattern's items, each with its span converted to UTF-16 indices over the
 * PATTERN. This is the parse structure the diagram is drawn from at step 6, and
 * for now the tester uses it only to count the parts of a pattern.
 */
export function enumerate(pattern, flags) {
  const answer = call("rx_enumerate", [pattern, flags]);

  if (answer.error) {
    return answer;
  }

  const map = byteToCharacterMap(pattern);

  return {
    items: answer.items.map(({ pos, len }) => ({ start: map[pos], end: map[pos + len] })),
  };
}

/**
 * **What the engine actually did, step by step.** Each step is
 * `{ item: {start, end}, at, from }`: which span of the PATTERN was being
 * tried, where in the subject, and where the ATTEMPT it belongs to started.
 * The last of those is what tells a bump along (the whole attempt failed, start
 * again one character later) apart from a backtrack (inside one attempt, give
 * text back and try another path). Reported as one thing they are
 * indistinguishable.
 *
 * Offsets are converted on both sides, because the two indexes are into
 * different strings: `item` indexes the pattern, `at` indexes the subject.
 *
 * **This is a separate run from `match`, and a slower one.** The engine has to
 * be compiled with its start-up optimisations off or it can decline to start at
 * all, so the trace is asked for deliberately rather than computed on every
 * keystroke.
 */
export function trace(pattern, subject, flags) {
  const answer = call("rx_trace", [pattern, subject, flags]);

  if (answer.error) {
    return answer;
  }

  const inPattern = byteToCharacterMap(pattern);
  const inSubject = byteToCharacterMap(subject);

  return {
    truncated: answer.truncated,
    outcome: answer.outcome,
    // **Every match, not the first.** Each carries the step count at which it
    // was found, so the caller can name the attempt that won without comparing
    // offsets - `\K` moves a match's reported start away from where its attempt
    // began, and an offset comparison would mislabel exactly the patterns that
    // most need explaining.
    matches: answer.matches.map(([from, to, after]) => ({
      start: inSubject[from],
      end: inSubject[to],
      afterStep: after,
    })),
    steps: answer.steps.map(([pos, len, at, from]) => ({
      item: { start: inPattern[pos], end: inPattern[pos + len] },
      at: inSubject[at],
      // Where the attempt this step belongs to began. Two steps with different
      // `from` values are in different attempts, which is a completely
      // different event from going backwards inside one.
      from: inSubject[from],
    })),
  };
}

/** What this was built against. Shown in the page's footnote. */
export function version() {
  return call("rx_version", []);
}
